import type { QueryKey, QueryFunctionContext, UseQueryOptions } from '@tanstack/react-query';
import {
  getMunicipality,
  getStreet,
  getHouseNumber,
  getBoxNumber,
  type PostalMunicipalityParams,
  PostalStreetParams,
  PostalHouseNumberParams,
  PostalBoxNumberParams,
} from './postal.api';
import type { OptionalQueryOptions, QueryApiOptions } from '../../types/types';
import { PostalBoxNumber, PostalHouseNumber, PostalMunicipality, PostalStreet } from '@as-react/common-types';

export const postalQueryKeys = {
  all: () => ['postal'] as QueryKey,
  municipality: ({ query, lang, city }: PostalMunicipalityParams) =>
    [...postalQueryKeys.all(), 'municipality', query, lang, city] as QueryKey,
  street: (params: PostalStreetParams) => [...postalQueryKeys.all(), 'street', params] as QueryKey,
  houseNumber: (params: PostalHouseNumberParams) => [...postalQueryKeys.all(), 'houseNumber', params] as QueryKey,
  boxNumber: (params: PostalBoxNumberParams) => [...postalQueryKeys.all(), 'boxNumber', params] as QueryKey,
};

export const postalQueries = (apiOptions: QueryApiOptions) => ({
  getMunicipality: <TReturnData>(
    params: PostalMunicipalityParams,
    options: OptionalQueryOptions<Array<PostalMunicipality>, TReturnData> = {}
  ) =>
    ({
      ...options,
      queryKey: postalQueryKeys.municipality(params),
      queryFn: (context: QueryFunctionContext) => getMunicipality(apiOptions, params, context),
    } as UseQueryOptions<Array<PostalMunicipality>, Error, TReturnData>),
  getStreet: <TReturnData>(
    params: PostalStreetParams,
    options: OptionalQueryOptions<Array<PostalStreet>, TReturnData> = {}
  ) =>
    ({
      ...options,
      queryKey: postalQueryKeys.street(params),
      queryFn: (context: QueryFunctionContext) => getStreet(apiOptions, params, context),
    } as UseQueryOptions<Array<PostalStreet>, Error, TReturnData>),
  getHouseNumber: <TReturnData>(
    params: PostalHouseNumberParams,
    options: OptionalQueryOptions<Array<PostalHouseNumber>, TReturnData> = {}
  ) =>
    ({
      ...options,
      queryKey: postalQueryKeys.houseNumber(params),
      queryFn: (context: QueryFunctionContext) => getHouseNumber(apiOptions, params, context),
    } as UseQueryOptions<Array<PostalHouseNumber>, Error, TReturnData>),
  getBoxNumber: <TReturnData>(
    params: PostalBoxNumberParams,
    options: OptionalQueryOptions<Array<PostalBoxNumber>, TReturnData> = {}
  ) =>
    ({
      ...options,
      queryKey: postalQueryKeys.boxNumber(params),
      queryFn: (context: QueryFunctionContext) => getBoxNumber(apiOptions, params, context),
    } as UseQueryOptions<Array<PostalBoxNumber>, Error, TReturnData>),
});
